import { PropsWithChildren } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaignItem } from 'api/campaigns';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AuctionItem } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ToggleHideListingItemProps extends PropsWithChildren {
    campaignId: number;
    item: AuctionItem;
}

const ToggleHideListingItem = (props: ToggleHideListingItemProps) => {
    const { item, campaignId, children } = props;
    const toast = useToast();
    const queryClient = useQueryClient();

    const isPrivateListing = item.visibility === 'private';
    const toggleLabelType = isPrivateListing ? 'Unhide' : 'Hide';

    const { mutate: toggleHideListing } = useMutation({
        mutationFn: () =>
            updateCampaignItem({
                campaignId,
                itemId: item.id,
                data: { visibility: isPrivateListing ? 'public' : 'private' },
            }),
        onSuccess: () => {
            toast.success(`Item is now ${isPrivateListing ? 'unhidden' : 'hidden'}!`);
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaignId) });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message);
        },
    });

    return (
        <AlertDialog.Root type="warning">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>{`${toggleLabelType} this listing?`}</AlertDialog.Title>
                        <AlertDialog.Description>
                            {isPrivateListing
                                ? 'Unhiding this listing will make it publicly viewable again.'
                                : 'Hidden listings will no longer be publicly viewable. The listing can still be accessed via a direct link.'}
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => toggleHideListing()}>
                            {`${toggleLabelType} listing`}
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const ToggleHideListingItemTrigger = AlertDialog.Trigger;

const Root = ToggleHideListingItem;
const Trigger = ToggleHideListingItemTrigger;

export {
    ToggleHideListingItem,
    ToggleHideListingItemTrigger,
    //
    Root,
    Trigger,
};
export type { ToggleHideListingItemProps };
